import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import { useTheme } from '../contexts/ThemeContext'; // Hidden until dark mode is ready
import { useRBAC } from '../hooks/useRBAC';
import { useHasLicense, useIsEvidenceAssigned } from '../contexts/LicenseContext';
import { useOrganizations } from '../contexts/OrganizationContext';
import { useShowUserGuide, useShowAiIntelligence, useRemediationWorkflows, useRemediationScorecard, useUsoc, useFusion } from '../contexts/FeatureFlagsContext';
import { remediationsNavItem, canAccessScorecard } from './remediationScorecardNav';
import Spider from "./SpiderIcon";
import { useDarkWebBadge } from "../hooks/useDarkWebBadge";
import { useModuleAvailability } from "../hooks/useModuleAvailability";

import {
  Home,
  FileText,
  CheckCircle,
  Shield,
  ShieldAlert,
  DollarSign,
  Users,
  User,
  LogOut,
  Settings,
  Globe,
  Building2,
  Handshake,
  Brain,
  Link2,
  ClipboardList,
  HelpCircle,
  BookOpen,
  Mail,
  Lock,
  BarChart2,
  BarChart3,
  Menu,
  X,
  FileSearch,
  Workflow,
  Network,
  Radar
} from 'lucide-react';
import { Button } from './Button';
import { GrcAlertBell } from './GrcAlertBell';

const Navigation = () => {
  const { user, logout, loading } = useAuth();
  // Theme toggle hidden until dark mode is ready
  // const { theme, toggleTheme, isDark } = useTheme();
  const { canAdministerOrganization, canAdministerAnyDealOrBusinessUnit, isSuperAdmin, isSupportAdmin, isAssignmentScopedOnly, canViewOrganizationAssessments, canManageOrganizationRisks, canManageSecurityInsights, canManageIntegrations, canManageWorkforceIntelligence, canRunAgent, canManageWorkflowTemplates, canViewOrganization } = useRBAC();

  // Super/support admins get read-only visibility into manager-gated modules
  // (GRC, Brand, Workforce). Write actions inside stay gated on canManage* checks.
  const isAdminViewer = isSuperAdmin() || isSupportAdmin();
  // No breadth anywhere in this org (SELF-scoped, or a deal/BU contributor/approver): the
  // whole browse-the-portfolio nav collapses to My Tasks. Name kept — it drives ~20 checks below.
  const selfScopeOnly = isAssignmentScopedOnly();
  const hasLicense = useHasLicense();
  // Contributor+ users with an evidence request assigned get the GRC entry too — the page
  // itself collapses to the assignee-only Evidence Requests view for them.
  const isEvidenceAssigned = useIsEvidenceAssigned();
  const showGRCFull = () => canManageSecurityInsights() || isAdminViewer;
  const showGRCEntry = () => showGRCFull() || isEvidenceAssigned;
  const organizations = useOrganizations();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [ransomwareOpen, setRansomwareOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(() => {
    try {
      const stored = localStorage.getItem("selectedOrganization");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const dropdownRef = useRef(null);
  const ransomwareRef = useRef(null);

  const enterprisesRef = useRef(null);
  const [enterprisesOpen, setEnterpriseOpen] = useState(false);

  const dealModuleRef = useRef(null);
  const [dealModuleOpen, setDealModuleOpen] = useState(false);

  const businessUnitModuleRef = useRef(null);
  const [businessUnitModuleOpen, setBusinessUnitModuleOpen] = useState(false);

  const darkWebRef = useRef(null);
  const [darkWebOpen, setDarkWebOpen] = useState(false);
  const { count: darkWebBadgeCount, resetCount: resetDarkWebBadge } = useDarkWebBadge(selectedOrg?.organizationId);

  const vendorRef = useRef(null);
  const [vendorOpen, setVendorOpen] = useState(false);

  const aiRef = useRef(null);
  const [aiOpen, setAiOpen] = useState(false);

  // Remediations becomes a dropdown (Remediations, Scorecard) when the scorecard is on.
  const remediationsRef = useRef(null);
  const [remediationsOpen, setRemediationsOpen] = useState(false);


  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if user can administer (has ADMIN role at ORG scope or above, or Deal/BU admin)
  const showUsersLink = canAdministerOrganization() || canAdministerAnyDealOrBusinessUnit();
  const showUserGuide = useShowUserGuide();
  const showAiIntelligence = useShowAiIntelligence();
  // Egress catalog (deployment profile): a module this installation cannot run (no internet access
  // on Restricted / AirGapped) is never offered. Items wait for the flags to load so an unavailable
  // module is not shown and then removed.
  const { loaded: moduleFlagsLoaded, isModuleAvailable } = useModuleAvailability();
  const moduleShown = (id) => moduleFlagsLoaded && isModuleAvailable(id);
  const showDarkWebMonitoring = moduleShown('darkWebMonitoring');
  const showSurfaceScan = moduleShown('surfaceScan');
  const showSecretsScanning = moduleShown('secretsScanning');
  const showBrandProtection = moduleShown('brandProtection');
  const showAiIntelligenceModule = showAiIntelligence && moduleShown('aiIntelligence');
  // Features:DefenderIdentity gates the whole identity surface; the nav item follows it so the
  // page is never reachable from a menu when the data behind it is switched off.
  const usocEnabled = useUsoc();
  // Features:Fusion gates the Fusion tab; the page and API follow the same flag.
  const fusionEnabled = useFusion();
  // Features:RemediationWorkflows (UI-only): gates the Settings → Workflows entries.
  const showWorkflows = useRemediationWorkflows();
  // Per-org switch plus organization access, the same check as the route guard.
  const scorecardEnabled = useRemediationScorecard();
  const showScorecard = scorecardEnabled && canAccessScorecard({ isSuperAdmin, isSupportAdmin, canViewOrganization });

  // Check licensed products for navigation visibility
  const showDeals = hasLicense('Deals');
  const showBusinessUnits = hasLicense('BusinessUnits');
  const showEnterprise = hasLicense('Enterprise');
  const showTPRM = hasLicense('TPRM');
  const showRansomware = hasLicense('Ransomware');
  const showDarkWeb = hasLicense('DarkWeb');
  const showAI = hasLicense('AI');
  const showEnterpriseDeals = hasLicense('EnterpriseDeals');
  const showEnterpriseBusinessUnits = hasLicense('EnterpriseBusinessUnits');
  const showGRC = hasLicense('GRC');
  const showSecurityOps = usocEnabled && showGRC && showGRCFull();


  // Close dropdowns when clicking outside.
  // NOTE: must run BEFORE any conditional early return (e.g. the `loading` guard below) — a hook after
  // an early return changes the hook count when `loading` flips and throws "Rendered fewer hooks than
  // expected" (React rules of hooks). This surfaced when session invalidation flipped the auth state.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
      if (enterprisesRef.current && !enterprisesRef.current.contains(event.target)) {
        setEnterpriseOpen(false);
      }
      if (dealModuleRef.current && !dealModuleRef.current.contains(event.target)) {
        setDealModuleOpen(false);
      }
      if (businessUnitModuleRef.current && !businessUnitModuleRef.current.contains(event.target)) {
        setBusinessUnitModuleOpen(false);
      }
      if (ransomwareRef.current && !ransomwareRef.current.contains(event.target)) {
        setRansomwareOpen(false);
      }
      if (darkWebRef.current && !darkWebRef.current.contains(event.target)) {
        setDarkWebOpen(false);
      }
      if (vendorRef.current && !vendorRef.current.contains(event.target)) {
        setVendorOpen(false);
      }
      if (aiRef.current && !aiRef.current.contains(event.target)) {
        setAiOpen(false);
      }
      if (remediationsRef.current && !remediationsRef.current.contains(event.target)) {
        setRemediationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  //if (loading) return null;
  if (loading) return <div className="h-16" />;

  const handleLogout = () => {
    navigate('/logout');
  };

  // Split around the Deals and Business Units dropdowns (rendered separately below) so each renders
  // in its original flat-link position — Deals then Business Units, right after Dashboard — rather
  // than after the whole group.
  const navigationItemsBeforeBU = [
    ...(!selfScopeOnly ? [{ path: '/dashboard', label: 'Dashboard', icon: Home }] : []),
    ...(showSecurityOps ? [{ path: '/soc', label: 'USOC', icon: Radar }] : []),
    ...(selfScopeOnly ? [{ path: '/my-tasks', label: 'My Tasks', icon: ClipboardList }] : []),
  ];
  const navigationItemsAfterBU = [
    ...(fusionEnabled && !selfScopeOnly ? [{ path: '/Fusion', label: 'Fusion', icon: Network }] : []),
    { path: '/risks', label: 'Risks', icon: ShieldAlert },
    remediationsNavItem(showScorecard, { remediationsIcon: CheckCircle, scorecardIcon: BarChart3 }),
    { path: '/riskRegister', label: 'Risk Register', icon: Shield },
    ...(selfScopeOnly && canManageIntegrations() ? [{ path: '/settings/integrations', label: 'Integrations', icon: Link2 }] : []),
    ...((canManageWorkforceIntelligence() || isAdminViewer) ? [{ path: '/WorkforceIntelligence', label: 'Workforce', icon: BarChart2 }] : []),
  ];
  const navigationItems = [...navigationItemsBeforeBU, ...navigationItemsAfterBU];

  // The whole block (menu, mobile panel, organization strip) is pinned while the page scrolls, on
  // every page. Anything else sticky on a page, or any scrollIntoView target, sits under it using
  // the offsets in PinnedNavigation.js.
  return (
    <div data-app-nav-shell className="sticky top-0 z-50 bg-theme-secondary">
    <nav data-app-nav className="bg-theme-secondary/80 backdrop-blur-xl shadow-theme-lg border-b border-border relative z-50">
      <div className="w-full px-4">
        <div className="flex items-center h-16 gap-2">
          {/* Left: Logo + Hamburger */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0 pr-1">
              {canRunAgent() ? (
                <button onClick={() => navigate('/agent')} className="logo-wordmark">
                  <span className="logo-wordmark-text logo-wordmark-text-gradient">AXARIS™</span>
                </button>
              ) : (
                <span className="logo-wordmark">
                  <span className="logo-wordmark-text logo-wordmark-text-gradient">AXARIS™</span>
                </span>
              )}
            </div>

            {user && (
              <button
                onClick={() => setMobileMenuOpen(prev => !prev)}
                className="xl:hidden p-2 rounded-lg text-content-secondary hover:text-content-primary hover:bg-theme-hover transition-colors flex-shrink-0"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
          </div>

          {/* Center: Nav items — desktop only (1280px+) */}
          {user && (
            <div className="flex-1 hidden xl:flex justify-center">
              <div className="flex items-center space-x-1">
                {navigationItemsBeforeBU.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isActive
                        ? 'bg-accent-cyan/10 text-accent-cyan border-b-2 border-accent-cyan'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <IconComponent className="h-4 w-4 flex-shrink-0" />
                      <span className="leading-none">{item.label}</span>
                    </button>
                  );
                })}

                {/* Deals Dropdown — was a flat link to /deals; now a dropdown so the Deal dashboard
                    has a home in the same menu slot instead of a second, separate entry. */}
                {!selfScopeOnly && showDeals && (
                  <div className="relative" ref={dealModuleRef}>
                    <button
                      onClick={() => setDealModuleOpen(prev => !prev)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${['/deals', '/dealdashboard'].includes(location.pathname.toLowerCase())
                        ? 'bg-accent-cyan/10 text-accent-cyan border-b-2 border-accent-cyan'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      <span className="leading-none">Deals</span>
                    </button>

                    {dealModuleOpen && (
                      <div className="absolute mt-2 w-48 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                        <button
                          onClick={() => { navigate('/DealDashboard'); setDealModuleOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                        >
                          Dashboard
                        </button>
                        <button
                          onClick={() => { navigate('/deals'); setDealModuleOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          Deals
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Business Units Dropdown — was a flat link to /businessunits; now a dropdown so
                    the dashboard has a home without adding a second, separate "Business Unit" entry
                    elsewhere in the bar. */}
                {!selfScopeOnly && showBusinessUnits && (
                  <div className="relative" ref={businessUnitModuleRef}>
                    <button
                      onClick={() => setBusinessUnitModuleOpen(prev => !prev)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${['/businessunits', '/businessunitdashboard'].includes(location.pathname.toLowerCase())
                        ? 'bg-accent-cyan/10 text-accent-cyan border-b-2 border-accent-cyan'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <Building2 className="h-4 w-4 flex-shrink-0" />
                      <span className="leading-none">Business Units</span>
                    </button>

                    {businessUnitModuleOpen && (
                      <div className="absolute mt-2 w-48 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                        <button
                          onClick={() => { navigate('/BusinessUnitDashboard'); setBusinessUnitModuleOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                        >
                          Dashboard
                        </button>
                        <button
                          onClick={() => { navigate('/businessunits'); setBusinessUnitModuleOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          Business Units
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {navigationItemsAfterBU.map((item) => {
                  const IconComponent = item.icon;
                  if (item.children) {
                    const isActive = item.children.some(child => location.pathname === child.path);
                    return (
                      <div key={item.path} className="relative" ref={remediationsRef}>
                        <button
                          onClick={() => setRemediationsOpen(prev => !prev)}
                          className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isActive
                            ? 'bg-accent-cyan/10 text-accent-cyan border-b-2 border-accent-cyan'
                            : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                            }`}
                        >
                          <IconComponent className="h-4 w-4 flex-shrink-0" />
                          <span className="leading-none">{item.label}</span>
                        </button>

                        {remediationsOpen && (
                          <div className="absolute mt-2 w-48 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                            {item.children.map(child => (
                              <button
                                key={child.path}
                                onClick={() => { navigate(child.path); setRemediationsOpen(false); }}
                                className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                              >
                                {child.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isActive
                        ? 'bg-accent-cyan/10 text-accent-cyan border-b-2 border-accent-cyan'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <IconComponent className="h-4 w-4 flex-shrink-0" />
                      <span className="leading-none">{item.label}</span>
                    </button>
                  );
                })}

                {/* GRC + Bell (separate clickable elements) */}
                {showGRC && showGRCEntry() && (
                  <div className="flex items-center">
                    <button
                      onClick={() => navigate('/SecurityInsights')}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-l-md text-sm font-medium transition-colors whitespace-nowrap ${
                        location.pathname === '/SecurityInsights'
                          ? 'bg-accent-cyan/10 text-accent-cyan border-b-2 border-accent-cyan'
                          : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                      }`}
                    >
                      <Lock className="h-4 w-4 flex-shrink-0" />
                      <span className="leading-none">GRC</span>
                    </button>
                    {/* Alerts are org-wide GRC detail — evidence-only assignees don't get the bell */}
                    {showGRCFull() && <GrcAlertBell orgId={selectedOrg?.organizationId} />}
                  </div>
                )}

                {/* Brand Protection */}
                {showBrandProtection && showGRC && (canManageIntegrations() || isAdminViewer) && (
                  <button
                    onClick={() => navigate('/BrandProtection')}
                    className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      location.pathname === '/BrandProtection'
                        ? 'bg-orange-100 text-orange-700 border-b-2 border-orange-600'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                    }`}
                  >
                    <Shield className="h-4 w-4 flex-shrink-0" />
                    <span className="leading-none">Brand</span>
                  </button>
                )}

                {/* Enterprise Dropdown */}
                {!selfScopeOnly && showEnterprise && (
                  <div className="relative" ref={enterprisesRef}>
                    <button
                      onClick={() => setEnterpriseOpen(prev => !prev)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${location.pathname.includes('enterprise')
                        ? 'bg-accent-cyan/10 text-accent-cyan border-b-2 border-accent-cyan'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <Building2 className="w-5 h-5" />
                      <span className="text-status-success">Enterprises</span>
                    </button>

                    {enterprisesOpen && (
                      <div className="absolute mt-2 w-48 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                        <button
                          onClick={() => { navigate('/EnterpriseDashboard'); setEnterpriseOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                        >
                          Dashboard
                        </button>
                        {canViewOrganizationAssessments() && (
                          <button
                            onClick={() => { navigate(`/EnterpriseOrganizations`); setEnterpriseOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Organization Assessments
                          </button>
                        )}
                        {showDeals && showEnterpriseDeals && (
                          <button
                            onClick={() => { navigate('/EnterpriseDeals'); setEnterpriseOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Deals
                          </button>
                        )}
                        {showBusinessUnits && showEnterpriseBusinessUnits && (
                          <button
                            onClick={() => { navigate('/EnterpriseBusinessUnits'); setEnterpriseOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Business Units
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Vendor (TPRM) Dropdown */}
                {!selfScopeOnly && showTPRM && (
                  <div className="relative" ref={vendorRef}>
                    <button
                      onClick={() => setVendorOpen(prev => !prev)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${location.pathname.includes('vendor')
                        ? 'bg-green-600/10 text-green-600 border-b-2 border-green-600'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <Handshake className="h-4 w-4 flex-shrink-0" />
                      <span className="text-green-600">TPRM</span>
                    </button>

                    {vendorOpen && (
                      <div className="absolute mt-2 w-48 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                        <button
                          onClick={() => { navigate('/TPRMDashboard'); setVendorOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          Dashboard
                        </button>
                        {canViewOrganizationAssessments() && (
                          <button
                            onClick={() => { navigate('/TPRMPortfolio'); setVendorOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Vendor Portfolio
                          </button>
                        )}
                        {canViewOrganizationAssessments() && (
                          <button
                            onClick={() => { navigate('/TPRMOrganizations'); setVendorOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Enterprise Assessments
                          </button>
                        )}
                        {showDeals && (
                          <button
                            onClick={() => { navigate('/TPRMDeals'); setVendorOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Deals
                          </button>
                        )}
                        {showBusinessUnits && (
                          <button
                            onClick={() => { navigate('/TPRMBusinessUnits'); setVendorOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Business Units
                          </button>
                        )}
                        <button
                          onClick={() => { navigate('/TPRMApplications'); setVendorOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          Vendor Applications
                        </button>
                      </div>
                    )}
                  </div>
                )}



                {/* Ransomware Dropdown */}
                {!selfScopeOnly && showRansomware && (
                  <div className="relative" ref={ransomwareRef}>
                    <button
                      onClick={() => setRansomwareOpen(prev => !prev)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${location.pathname.includes('ransomware')
                        ? 'bg-risk-critical/10 text-risk-critical border-b-2 border-risk-critical'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <ShieldAlert className="h-4 w-4 flex-shrink-0" />
                      <span className="text-risk-critical">Ransomware</span>
                    </button>

                    {ransomwareOpen && (
                      <div className="absolute mt-2 w-48 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                        <button
                          onClick={() => { navigate('/RansomwareDashboard'); setRansomwareOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          Dashboard
                        </button>
                        {canViewOrganizationAssessments() && (
                          <button
                            onClick={() => { navigate('/RansomwareOrganizations'); setRansomwareOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Organization Assessment
                          </button>
                        )}
                        {showDeals && (
                          <button
                            onClick={() => { navigate('/RansomwareDeals'); setRansomwareOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Deals
                          </button>
                        )}
                        {showBusinessUnits && (
                          <button
                            onClick={() => { navigate('/RansomwareBusinessUnits'); setRansomwareOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Business Units
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* DarkWeb Dropdown */}
                {!selfScopeOnly && showDarkWeb && (
                  <div className="relative" ref={darkWebRef}>
                    <button
                      onClick={() => setDarkWebOpen(prev => !prev)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${location.pathname.includes('darkWeb')
                        ? 'bg-blue-800/10 text-blue-800 border-b-2 border-blue-800'
                        : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                    >
                      <span className="relative">
                        <Spider className="w-5 h-5" />
                        {darkWebBadgeCount > 0 && (
                          <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none bg-red-600 text-white">
                            {darkWebBadgeCount > 99 ? "99+" : darkWebBadgeCount}
                          </span>
                        )}
                      </span>
                      <span className="text-blue-800">Dark Web</span>
                    </button>

                    {darkWebOpen && (
                      <div className="absolute mt-2 w-56 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                        <button
                          onClick={() => { navigate('/DarkWebDashboard'); setDarkWebOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          Risk Dashboard
                        </button>
                        {showDarkWebMonitoring && (
                          <button
                            onClick={() => { navigate('/DarkWebMonitoringDashboard'); setDarkWebOpen(false); resetDarkWebBadge(); }}
                            className={`flex w-full items-center px-3 py-2 text-sm hover:text-content-primary hover:bg-theme-hover whitespace-nowrap ${
                              darkWebBadgeCount > 0 ? 'font-semibold text-red-700' : 'text-content-secondary'
                            }`}
                          >
                            {darkWebBadgeCount > 0 && <span className="mr-1.5 text-red-600">●</span>}
                            Live Monitoring Dashboard
                          </button>
                        )}
                        {showDarkWebMonitoring && (
                          <button
                            onClick={() => { navigate('/DarkWebDomainConfig'); setDarkWebOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Live Monitoring Config
                          </button>
                        )}
                        {showSecretsScanning && (
                          <button
                            onClick={() => { navigate('/DarkWebSecretsScan'); setDarkWebOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Secrets Scanning
                          </button>
                        )}
                        {showSurfaceScan && (
                          <button
                            onClick={() => { navigate('/DarkWebEsmDashboard'); setDarkWebOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Surface Scan
                          </button>
                        )}
                        {canViewOrganizationAssessments() && (
                          <button
                            onClick={() => { navigate('/DarkWebOrganizations'); setDarkWebOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Organization Assessment
                          </button>
                        )}
                        {showDeals && (
                          <button
                            onClick={() => { navigate('/DarkWebDeals'); setDarkWebOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Deals
                          </button>
                        )}
                        {showBusinessUnits && (
                          <button
                            onClick={() => { navigate('/DarkWebBusinessUnits'); setDarkWebOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Business Units
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* AI Dropdown */}
                {!selfScopeOnly && showAI && (
                  <div className="relative" ref={aiRef}>
                    <button
                      onClick={() => setAiOpen(prev => !prev)}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        location.pathname.includes('AI')
                          ? 'bg-purple-800/10 text-purple-800 border-b-2 border-purple-800'
                          : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                      }`}
                    >
                      <Brain className="w-5 h-5 text-purple-800" />
                      <span className="text-purple-800">AI</span>
                    </button>

                    {aiOpen && (
                      <div className="absolute mt-2 w-48 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                        <button
                          onClick={() => { navigate('/AIDashboard'); setAiOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          Dashboard
                        </button>
                        {showAiIntelligenceModule && (
                          <button
                            onClick={() => { navigate('/AIIntelligence'); setAiOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            AI Intelligence
                          </button>
                        )}
                        {canViewOrganizationAssessments() && (
                          <button
                            onClick={() => { navigate('/AIOrganizations'); setAiOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover whitespace-nowrap"
                          >
                            Organization Assessment
                          </button>
                        )}
                        {showDeals && (
                          <button
                            onClick={() => { navigate('/AIDeals'); setAiOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Deals
                          </button>
                        )}
                        {showBusinessUnits && (
                          <button
                            onClick={() => { navigate('/AIBusinessUnits'); setAiOpen(false); }}
                            className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                          >
                            Business Units
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Right group: Settings */}
          {user && (
            <div className="flex items-center flex-shrink-0 border-l border-border pl-3 gap-1 ml-auto">

                {/* Theme Toggle - hidden until dark mode is ready
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg text-content-secondary hover:text-content-primary hover:bg-theme-hover transition-colors"
                  title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDark ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                </button>
                */}

                {/* Settings Menu */}
                <div className="relative" ref={dropdownRef}>
                  <Button
                    onClick={() => setSettingsOpen(prev => !prev)}
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-1 whitespace-nowrap"
                  >
                    <Settings className="h-5 w-5 text-content-secondary" />
                  </Button>

                  {settingsOpen && (
                    <div className="absolute right-0 mt-2 w-44 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
                      <button
                        onClick={() => { navigate('/my-tasks'); setSettingsOpen(false); }}
                        className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                      >
                        <ClipboardList className="h-4 w-4 mr-2" />
                        My Tasks
                      </button>
                      <button
                        onClick={() => { navigate('/accounts'); setSettingsOpen(false); }}
                        className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        My Account
                      </button>
                      <button
                        onClick={() => { navigate('/organizations'); setSettingsOpen(false); }}
                        className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Organizations
                      </button>
                      {canManageIntegrations() && (
                        <button
                          onClick={() => { navigate('/settings/integrations'); setSettingsOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Integrations
                        </button>
                      )}
                      {showWorkflows && canManageWorkflowTemplates() && (
                        <button
                          onClick={() => { navigate('/settings/workflows'); setSettingsOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          <Workflow className="h-4 w-4 mr-2" />
                          Workflows
                        </button>
                      )}
                      {canManageOrganizationRisks() && (
                        <button
                          onClick={() => { navigate('/settings/document-access'); setSettingsOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          <FileSearch className="h-4 w-4 mr-2" />
                          Document Access
                        </button>
                      )}
                      {showUsersLink && (
                        <button
                          onClick={() => { navigate('/users'); setSettingsOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Users
                        </button>
                      )}
                      <button
                        onClick={() => { navigate('/faq'); setSettingsOpen(false); }}
                        className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                      >
                        <HelpCircle className="h-4 w-4 mr-2" />
                        FAQ
                      </button>
                      {showUserGuide && (
                        <button
                          onClick={() => { navigate('/user-guide'); setSettingsOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                        >
                          <BookOpen className="h-4 w-4 mr-2" />
                          User Guide
                        </button>
                      )}
                      <button
                        onClick={() => { navigate('/contact'); setSettingsOpen(false); }}
                        className="flex w-full items-center px-3 py-2 text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Contact Us
                      </button>
                      {isSuperAdmin() && (
                        <button
                          onClick={() => { navigate('/SuperConfig'); setSettingsOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 font-medium"
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          Platform Config
                        </button>
                      )}
                      {isSuperAdmin() && (
                        <button
                          onClick={() => { navigate('/super-admin'); setSettingsOpen(false); }}
                          className="flex w-full items-center px-3 py-2 text-sm text-accent-cyan hover:text-content-primary hover:bg-theme-hover"
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          Super Admin
                        </button>
                      )}
                      <button
                        onClick={() => { handleLogout(); setSettingsOpen(false); }}
                        className="flex w-full items-center px-3 py-2 text-sm text-risk-critical hover:bg-risk-critical-bg"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
          )}
        </div>
      </div>
    </nav>

    {/* Mobile menu panel — shown below xl when hamburger is open */}
    {user && mobileMenuOpen && (
      <div className="xl:hidden bg-theme-secondary border-b border-border z-40 shadow-theme-lg">
        <div className="px-4 py-3 space-y-1 max-h-[calc(100vh-7.25rem)] overflow-y-auto">
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            if (item.children) {
              return (
                <div key={item.path}>
                  <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">{item.label}</p>
                  {item.children.map(child => {
                    const ChildIcon = child.icon;
                    return (
                      <button
                        key={child.path}
                        onClick={() => { navigate(child.path); setMobileMenuOpen(false); }}
                        className={`flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm transition-colors ${location.pathname === child.path
                          ? 'bg-accent-cyan/10 text-accent-cyan'
                          : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                        }`}
                      >
                        <ChildIcon className="h-4 w-4 flex-shrink-0" />
                        <span>{child.label}</span>
                      </button>
                    );
                  })}
                </div>
              );
            }
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                className={`flex w-full items-center space-x-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive
                  ? 'bg-accent-cyan/10 text-accent-cyan'
                  : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
                }`}
              >
                <IconComponent className="h-4 w-4 flex-shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
          {showBrandProtection && showGRC && (canManageIntegrations() || isAdminViewer) && (
            <button
              onClick={() => { navigate('/BrandProtection'); setMobileMenuOpen(false); }}
              className={`flex w-full items-center space-x-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                location.pathname === '/BrandProtection'
                  ? 'bg-orange-50 text-orange-700'
                  : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
              }`}
            >
              <Shield className="h-4 w-4 flex-shrink-0" />
              <span>Brand Protection</span>
            </button>
          )}
          {showGRC && showGRCEntry() && (
            <button
              onClick={() => { navigate('/SecurityInsights'); setMobileMenuOpen(false); }}
              className={`flex w-full items-center space-x-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                location.pathname === '/SecurityInsights'
                  ? 'bg-accent-cyan/10 text-accent-cyan'
                  : 'text-content-secondary hover:text-content-primary hover:bg-theme-hover'
              }`}
            >
              <Lock className="h-4 w-4 flex-shrink-0" />
              <span>GRC</span>
            </button>
          )}

          {!selfScopeOnly && showDeals && (
            <div>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">Deals</p>
              <button onClick={() => { navigate('/DealDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><FileText className="h-4 w-4 flex-shrink-0" /><span>Dashboard</span></button>
              <button onClick={() => { navigate('/deals'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><FileText className="h-4 w-4 flex-shrink-0" /><span>Deals</span></button>
            </div>
          )}

          {!selfScopeOnly && showBusinessUnits && (
            <div>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">Business Units</p>
              <button onClick={() => { navigate('/BusinessUnitDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Dashboard</span></button>
              <button onClick={() => { navigate('/businessunits'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Business Units</span></button>
            </div>
          )}

          {!selfScopeOnly && showEnterprise && (
            <div>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">Enterprise</p>
              <button onClick={() => { navigate('/EnterpriseDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Dashboard</span></button>
              {canViewOrganizationAssessments() && <button onClick={() => { navigate('/EnterpriseOrganizations'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Org Assessments</span></button>}
              {showDeals && showEnterpriseDeals && <button onClick={() => { navigate('/EnterpriseDeals'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><FileText className="h-4 w-4 flex-shrink-0" /><span>Enterprise Deals</span></button>}
              {showBusinessUnits && showEnterpriseBusinessUnits && <button onClick={() => { navigate('/EnterpriseBusinessUnits'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Enterprise BUs</span></button>}
            </div>
          )}

          {!selfScopeOnly && showTPRM && (
            <div>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">TPRM</p>
              <button onClick={() => { navigate('/TPRMDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Handshake className="h-4 w-4 flex-shrink-0" /><span>Dashboard</span></button>
              {canViewOrganizationAssessments() && <button onClick={() => { navigate('/TPRMPortfolio'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Handshake className="h-4 w-4 flex-shrink-0" /><span>Vendor Portfolio</span></button>}
              {canViewOrganizationAssessments() && <button onClick={() => { navigate('/TPRMOrganizations'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Handshake className="h-4 w-4 flex-shrink-0" /><span>Enterprise Assessments</span></button>}
              {showDeals && <button onClick={() => { navigate('/TPRMDeals'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><FileText className="h-4 w-4 flex-shrink-0" /><span>Deals</span></button>}
              {showBusinessUnits && <button onClick={() => { navigate('/TPRMBusinessUnits'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Business Units</span></button>}
              <button onClick={() => { navigate('/TPRMApplications'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Handshake className="h-4 w-4 flex-shrink-0" /><span>Vendor Applications</span></button>
            </div>
          )}

          {!selfScopeOnly && showRansomware && (
            <div>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">Ransomware</p>
              <button onClick={() => { navigate('/RansomwareDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><ShieldAlert className="h-4 w-4 flex-shrink-0" /><span>Dashboard</span></button>
              {canViewOrganizationAssessments() && <button onClick={() => { navigate('/RansomwareOrganizations'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><ShieldAlert className="h-4 w-4 flex-shrink-0" /><span>Org Assessment</span></button>}
              {showDeals && <button onClick={() => { navigate('/RansomwareDeals'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><FileText className="h-4 w-4 flex-shrink-0" /><span>Deals</span></button>}
              {showBusinessUnits && <button onClick={() => { navigate('/RansomwareBusinessUnits'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Business Units</span></button>}
            </div>
          )}

          {!selfScopeOnly && showDarkWeb && (
            <div>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">Dark Web</p>
              <button onClick={() => { navigate('/DarkWebDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Globe className="h-4 w-4 flex-shrink-0" /><span>Risk Dashboard</span></button>
              {showDarkWebMonitoring && <button onClick={() => { navigate('/DarkWebMonitoringDashboard'); setMobileMenuOpen(false); resetDarkWebBadge(); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Globe className="h-4 w-4 flex-shrink-0" /><span>Live Monitoring</span></button>}
              {showDarkWebMonitoring && <button onClick={() => { navigate('/DarkWebDomainConfig'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Globe className="h-4 w-4 flex-shrink-0" /><span>Monitoring Config</span></button>}
              {showSecretsScanning && <button onClick={() => { navigate('/DarkWebSecretsScan'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Globe className="h-4 w-4 flex-shrink-0" /><span>Secrets Scanning</span></button>}

              {showSurfaceScan && <button onClick={() => { navigate('/DarkWebEsmDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Globe className="h-4 w-4 flex-shrink-0" /><span>Surface Scan</span></button>}
              {canViewOrganizationAssessments() && <button onClick={() => { navigate('/DarkWebOrganizations'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Globe className="h-4 w-4 flex-shrink-0" /><span>Org Assessment</span></button>}
              {showDeals && <button onClick={() => { navigate('/DarkWebDeals'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><FileText className="h-4 w-4 flex-shrink-0" /><span>Deals</span></button>}
              {showBusinessUnits && <button onClick={() => { navigate('/DarkWebBusinessUnits'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Business Units</span></button>}
            </div>
          )}

          {!selfScopeOnly && showAI && (
            <div>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold text-content-muted uppercase tracking-wider">AI</p>
              <button onClick={() => { navigate('/AIDashboard'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Brain className="h-4 w-4 flex-shrink-0" /><span>Dashboard</span></button>
              {showAiIntelligenceModule && <button onClick={() => { navigate('/AIIntelligence'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Brain className="h-4 w-4 flex-shrink-0" /><span>AI Intelligence</span></button>}
              {canViewOrganizationAssessments() && <button onClick={() => { navigate('/AIOrganizations'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Brain className="h-4 w-4 flex-shrink-0" /><span>Org Assessment</span></button>}
              {showDeals && <button onClick={() => { navigate('/AIDeals'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><FileText className="h-4 w-4 flex-shrink-0" /><span>Deals</span></button>}
              {showBusinessUnits && <button onClick={() => { navigate('/AIBusinessUnits'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>Business Units</span></button>}
            </div>
          )}


          <div className="border-t border-border pt-2 mt-2 space-y-1">
            <button onClick={() => { navigate('/my-tasks'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><ClipboardList className="h-4 w-4 flex-shrink-0" /><span>My Tasks</span></button>
            <button onClick={() => { navigate('/accounts'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Building2 className="h-4 w-4 flex-shrink-0" /><span>My Account</span></button>
            <button onClick={() => { navigate('/organizations'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Users className="h-4 w-4 flex-shrink-0" /><span>Organizations</span></button>
            {canManageIntegrations() && <button onClick={() => { navigate('/settings/integrations'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Link2 className="h-4 w-4 flex-shrink-0" /><span>Integrations</span></button>}
            {showWorkflows && canManageWorkflowTemplates() && <button onClick={() => { navigate('/settings/workflows'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Workflow className="h-4 w-4 flex-shrink-0" /><span>Workflows</span></button>}
            {showUsersLink && <button onClick={() => { navigate('/users'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Users className="h-4 w-4 flex-shrink-0" /><span>Users</span></button>}
            <button onClick={() => { navigate('/faq'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><HelpCircle className="h-4 w-4 flex-shrink-0" /><span>FAQ</span></button>
            {showUserGuide && <button onClick={() => { navigate('/user-guide'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><BookOpen className="h-4 w-4 flex-shrink-0" /><span>User Guide</span></button>}
            <button onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-content-secondary hover:text-content-primary hover:bg-theme-hover"><Mail className="h-4 w-4 flex-shrink-0" /><span>Contact Us</span></button>
            <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex w-full items-center space-x-3 px-3 py-2 rounded-md text-sm text-risk-critical hover:bg-risk-critical-bg"><LogOut className="h-4 w-4 flex-shrink-0" /><span>Logout</span></button>
          </div>
        </div>
      </div>
    )}

    {user && selectedOrg && (
      <div className="relative z-40 border-b border-border/50">
        <div className="w-full px-4">
          <div className="flex justify-between items-center h-8">
            <div className="flex items-center space-x-2 min-w-0">
              <Building2 className="h-3.5 w-3.5 text-content-secondary flex-shrink-0" />
              <span className="text-xs text-content-secondary truncate">{selectedOrg.organizationName}</span>
              {organizations.length > 1 && (
                <>
                  <span className="text-xs text-content-muted">|</span>
                  <button
                    onClick={() => navigate('/organizations')}
                    className="text-xs text-accent-cyan hover:text-accent-cyan/80 transition-colors flex-shrink-0"
                  >
                    Change
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <User className="h-3.5 w-3.5 text-content-secondary flex-shrink-0" />
              <span className="text-xs text-content-secondary truncate max-w-48">{user?.userName}</span>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default Navigation;
